/**
 * ChatWidget.jsx — Floating Chatbot Widget (Multi-Agent)
 *
 * A Chatbase-style floating chat widget that sits in the bottom-right corner.
 * Toggle open/close via a FAB button. Contains the full chat experience
 * (messages, screening quiz, disambiguation, step tracker) in a compact popup.
 *
 * Multi-agent features:
 * - Agent selector dropdown in header
 * - Per-agent conversation history (isolated)
 * - Dynamic starter questions based on selected agent
 * - Sends agentId + agentType with every request
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import MessageBubble from "./components/MessageBubble";
import StepTracker from "./components/StepTracker";
import InputBar from "./components/InputBar";
import { streamChatMessage, fetchAgents, fetchChatHistory, clearChatHistory } from "./chatApi";
import "./ChatWidget.css";

// Fallback prompts — used only before agents are loaded from backend
const FALLBACK_PROMPTS = [
  "Show me candidates with Python skills",
  "List all active jobs",
  "How many interviews are scheduled today?",
];

// Prefixes for interactive flows
const SCREENING_PREFIX = "SCREENING_ANSWERS:";
const CANDIDATE_SELECTED_PREFIX = "CANDIDATE_SELECTED:";
const JOB_SELECTED_PREFIX = "JOB_SELECTED:";
const INTERVIEWER_SELECTED_PREFIX = "INTERVIEWER_SELECTED:";

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pipelineSteps, setPipelineSteps] = useState([]);
  const [error, setError] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [hasTableData, setHasTableData] = useState(false);
  const bottomRef = useRef(null);

  // ── Multi-agent state ──
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // Per-agent conversation histories: { [agentKey]: messages[] }
  const [agentHistories, setAgentHistories] = useState({});

  // Current agent's messages (derived)
  const agentKey = selectedAgent
    ? `${selectedAgent.agentType}:${selectedAgent.id}`
    : "default";
  const messages = agentHistories[agentKey] || [];

  // Set messages for the current agent
  const setMessages = useCallback(
    (updater) => {
      setAgentHistories((prev) => ({
        ...prev,
        [agentKey]: typeof updater === "function"
          ? updater(prev[agentKey] || [])
          : updater,
      }));
    },
    [agentKey]
  );

  // Track if any message has table data for auto-expand
  const handleHasTable = useCallback((isTable) => {
    if (isTable) setHasTableData(true);
  }, []);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, pipelineSteps]);

  // Fetch agents on mount (default + custom)
  useEffect(() => {
    fetchAgents().then((data) => {
      if (Array.isArray(data) && data.length > 0) {
        setAgents(data);
        // Default to first agent (Master Agent)
        if (!selectedAgent && data.length > 0) {
          setSelectedAgent(data[0]);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load chat history from DB when agent changes
  useEffect(() => {
    if (!selectedAgent) return;

    const key = `${selectedAgent.agentType}:${selectedAgent.id}`;

    // Skip if we already loaded history for this agent
    if (agentHistories[key] !== undefined) return;

    setIsLoadingHistory(true);
    fetchChatHistory(selectedAgent.id, selectedAgent.agentType)
      .then((result) => {
        if (result.session) {
          setSessionId(result.session.id);
        }
        const loadedMessages = (result.messages || []).map((msg) => ({
          role: msg.role,
          content: msg.content,
          data: msg.data ? (typeof msg.data === "string" ? JSON.parse(msg.data) : msg.data) : null,
          timestamp: msg.createdAt || new Date().toISOString(),
        }));
        setAgentHistories((prev) => ({
          ...prev,
          [key]: loadedMessages,
        }));
      })
      .catch((err) => {
        console.warn("Failed to load chat history:", err);
        setAgentHistories((prev) => ({
          ...prev,
          [key]: [],
        }));
      })
      .finally(() => setIsLoadingHistory(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent]);

  const toggleWidget = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) setHasUnread(false);
  };

  // Get starter questions for the current agent
  const starterQuestions = selectedAgent?.starterQuestions?.length > 0
    ? selectedAgent.starterQuestions
    : FALLBACK_PROMPTS;

  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim() || isLoading) return;
      setError(null);
      setPipelineSteps([]);

      const userMsg = {
        role: "user",
        content: text,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        // Backend loads history from DB — no need to send it
        const finalResponse = await streamChatMessage(
          text,
          [],
          (event) => {
            if (event.step !== "done") {
              setPipelineSteps((prev) => [...prev, event]);
            }
          },
          {
            agentId: selectedAgent?.id || null,
            agentType: selectedAgent?.agentType || "DEFAULT",
            sessionId: sessionId || null,
          }
        );

        const botMsg = {
          role: "assistant",
          content: finalResponse.reply || "I couldn't process that. Please try again.",
          data: finalResponse.data || null,
          action: finalResponse.action || null,
          timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, botMsg]);
        if (!isOpen) setHasUnread(true);
      } catch (err) {
        setError(
          err.message.includes("Failed to fetch")
            ? "Cannot reach the HireNeuron backend. Make sure the server is running."
            : err.message
        );
      } finally {
        setIsLoading(false);
        setPipelineSteps([]);
      }
    },
    [messages, isLoading, isOpen, selectedAgent, setMessages, sessionId]
  );

  /**
   * Handle interactive UI submissions (screening quiz, disambiguation).
   */
  const handleInteractiveSubmit = useCallback(
    async (payload) => {
      if (isLoading) return;
      setError(null);
      setPipelineSteps([]);

      let prefix = "";
      let userMsgContent = "";

      if (payload.type === "candidate_disambiguation") {
        prefix = CANDIDATE_SELECTED_PREFIX;
        userMsgContent = "👤 Selected Candidate";
      } else if (payload.type === "job_disambiguation") {
        prefix = JOB_SELECTED_PREFIX;
        userMsgContent = "💼 Selected Job";
      } else if (payload.type === "interviewer_disambiguation") {
        prefix = INTERVIEWER_SELECTED_PREFIX;
        userMsgContent = `👤 Selected Interviewer: ${payload.selectedName || ""}`;
      } else {
        prefix = SCREENING_PREFIX;
        userMsgContent = "📝 Submitted screening answers for evaluation.";
      }

      const userMsg = {
        role: "user",
        content: userMsgContent,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const messageToSend = `${prefix}${JSON.stringify(payload)}`;

        // Backend loads history from DB — no need to send it
        const finalResponse = await streamChatMessage(
          messageToSend,
          [],
          (event) => {
            if (event.step !== "done") {
              setPipelineSteps((prev) => [...prev, event]);
            }
          },
          {
            agentId: selectedAgent?.id || null,
            agentType: selectedAgent?.agentType || "DEFAULT",
            sessionId: sessionId || null,
          }
        );

        const botMsg = {
          role: "assistant",
          content: finalResponse.reply || "Process complete.",
          data: finalResponse.data || null,
          action: finalResponse.action || null,
          timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, botMsg]);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
        setPipelineSteps([]);
      }
    },
    [messages, isLoading, selectedAgent, setMessages, sessionId]
  );

  const clearChat = async () => {
    // Delete session from database
    if (sessionId) {
      try {
        await clearChatHistory(sessionId);
      } catch (err) {
        console.warn("Failed to clear chat history from DB:", err);
      }
    }
    setMessages([]);
    setSessionId(null);
    setError(null);
    setPipelineSteps([]);
    setHasTableData(false);
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setShowAgentSelector(false);
    setError(null);
    setPipelineSteps([]);
    setHasTableData(false);
    setSessionId(null);
    // History for this agent will be loaded from DB by the useEffect
  };

  return (
    <div className="cw-container">
      {/* ── Chat Popup ── */}
      <div className={`cw-popup ${isOpen ? "cw-popup-open" : "cw-popup-closed"} ${hasTableData ? "cw-popup-expanded" : ""}`}>
        {/* Header */}
        <header className="cw-header">
          <div className="cw-header-left">
            <div className="cw-header-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8V4H8" />
                <rect width="16" height="12" x="4" y="8" rx="2" />
                <path d="M15 13v2" />
                <path d="M9 13v2" />
              </svg>
            </div>
            <div>
              <h1 className="cw-header-title">
                {selectedAgent ? selectedAgent.agentName : "AI Hiring Assistant"}
              </h1>
              <p className="cw-header-subtitle">Online</p>
            </div>
          </div>
          <div className="cw-header-actions">
            {/* Agent selector toggle */}
            {agents.length > 1 && (
              <button
                className="cw-action-btn"
                onClick={() => setShowAgentSelector((prev) => !prev)}
                title="Switch agent"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </button>
            )}
            {messages.length > 0 && (
              <button className="cw-action-btn" onClick={clearChat} title="Clear chat">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                </svg>
              </button>
            )}
            <button className="cw-action-btn" onClick={toggleWidget} title="Close chat">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </header>

        {/* Agent selector dropdown */}
        {showAgentSelector && (
          <div className="cw-agent-selector">
            {agents.map((agent) => (
              <button
                key={`${agent.agentType}:${agent.id}`}
                className={`cw-agent-option ${
                  selectedAgent?.id === agent.id && selectedAgent?.agentType === agent.agentType
                    ? "cw-agent-option-active"
                    : ""
                }`}
                onClick={() => handleAgentSelect(agent)}
              >
                <div className="cw-agent-option-name">{agent.agentName}</div>
                <div className="cw-agent-option-desc">{agent.description}</div>
                {agent.agentType === "CUSTOM" && (
                  <span className="cw-agent-badge">Custom</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Chat Body */}
        <main className="cw-body">
          {messages.length === 0 && !isLoading ? (
            /* Welcome state */
            <div className="cw-welcome">
              <div className="cw-welcome-icon-wrap">
                <div className="cw-welcome-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
              <h2 className="cw-welcome-title">
                {selectedAgent ? selectedAgent.agentName : "HireNeuron AI"}
              </h2>
              <p className="cw-welcome-subtitle">
                {selectedAgent?.description || "How can I help you with hiring today?"}
              </p>
              <div className="cw-quick-prompts">
                {starterQuestions.map((p, i) => (
                  <button
                    key={i}
                    className="cw-quick-btn"
                    onClick={() => sendMessage(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Message list */
            <div className="cw-messages">
              {messages.map((msg, i) => (
                <MessageBubble
                  key={i}
                  message={msg}
                  onScreeningSubmit={handleInteractiveSubmit}
                  isLoading={isLoading}
                  onHasTable={handleHasTable}
                />
              ))}
              {isLoading && pipelineSteps.length > 0 && (
                <StepTracker steps={pipelineSteps} />
              )}
              {isLoading && pipelineSteps.length === 0 && (
                <div className="hn-message-row hn-bot-row">
                  <div className="hn-avatar hn-bot-avatar">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 8V4H8" />
                      <rect width="16" height="12" x="4" y="8" rx="2" />
                      <path d="M15 13v2" />
                      <path d="M9 13v2" />
                    </svg>
                  </div>
                  <div className="hn-bubble hn-bot-bubble hn-typing-bubble">
                    <span className="hn-dot" />
                    <span className="hn-dot" />
                    <span className="hn-dot" />
                  </div>
                </div>
              )}
              {error && (
                <div className="hn-error-banner">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </main>

        {/* Footer / Input */}
        <footer className="cw-footer">
          <InputBar onSend={sendMessage} disabled={isLoading} />
        </footer>
      </div>

      {/* ── FAB Toggle Button ── */}
      <button
        className={`cw-fab ${isOpen ? "cw-fab-active" : ""}`}
        onClick={toggleWidget}
        title="Chat with AI Assistant"
      >
        {hasUnread && <span className="cw-fab-badge" />}
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 8V4H8" />
            <rect width="16" height="12" x="4" y="8" rx="2" />
            <path d="M2 14h2" />
            <path d="M20 14h2" />
            <path d="M15 13v2" />
            <path d="M9 13v2" />
          </svg>
        )}
      </button>
    </div>
  );
}
